import * as React from "react";
import groupBy from "lodash.groupby";
import intersection from "lodash.intersection";
import { useInsights } from "../../../context/insights";
import { getOccurrences } from "../../../context/insights/actions";
import { composeOccurrences } from "../../../redux/modules/insights/utils";
import {
  sortInsights,
  groupOccurrencesIntoInsights,
  mapEnumToAnalysisGroupKey,
  mapSelectedCategoryToCategoryKey,
} from "../../../components/Analysis/utils/occurrenceTransforms";
import { GroupBy, Category } from "../../../components/Analysis/utils/types";

function indexOccurrencesById(
  occurrences: Insite.Insights.ComposedOccurrence[]
): IndexByNumber<Insite.Insights.ComposedOccurrence> {
  return occurrences.reduce<IndexByNumber<Insite.Insights.ComposedOccurrence>>(
    (group, occurrence) => {
      group[occurrence.occurrenceId] = occurrence;
      return group;
    },
    {}
  );
}

/**
 * A hook that requires everything for making a new request to the insights
 * endpoint. Under the hood it's merely a flow control wrapper around the base
 * `useInsights` hook that manages state and dispatch.
 *
 * @param timeRange
 * @param selectedSites
 * @param sites
 * @param projects
 */
export function useInsightsEffects(
  timeRange = 3,
  selectedSites: string[],
  shouldRequest = false,
  sites: IndexByString<Insite.Site>,
  projects: IndexByNumber<Insite.Project>
) {
  const [state, dispatch] = useInsights();

  // Retrieve initial data on first load and when time range changes
  React.useEffect(() => {
    if (shouldRequest && !state.isLoading) {
      void getOccurrences(dispatch, timeRange, selectedSites);
    }
  });

  // When the request has finished loading, compose occurrences
  React.useEffect(() => {
    if (!state.isLoaded || state.isLoading) return;

    const composedOccurrences = composeOccurrences(
      Object.values(state.rawData.hit),
      sites,
      projects
    );

    // Process siteGroups here as it's a one-off expensive operation
    const composedOccurrencesBySite = groupBy(
      composedOccurrences,
      (o) => o.siteId
    );
    dispatch({
      type: "SET_COMPOSED_GROUPED_OCCURRENCES",
      composedOccurrences,
      composedOccurrencesBySite,
    });
  }, [state.isLoaded, state.rawData, state.isLoading]);

  // Process initial data once retrieved
  React.useEffect(() => {
    if (!state.isComposed || state.isLoading) return;

    const filtered =
      state.composedFilteredOccurrences.length > 0
        ? state.composedFilteredOccurrences
        : state.composedOccurrences;

    // Group filtered occurrences into the current analysis group
    const composedOccurrencesById = indexOccurrencesById(filtered);
    const groupKey = mapEnumToAnalysisGroupKey(state.groupBy);
    const categoryKey = mapSelectedCategoryToCategoryKey(state.category);
    const composedAndGroupedOccurrences = Object.values(state.rawData[groupKey])
      .reduce<Insite.Insights.ComposedOccurrence[][]>((acc, ids) => {
        // remove occurrence ids that don't exist in the grouped key or in the
        // currently selected category
        const { category: categoryData } = state.rawData;
        const categorisedKeys =
          state.category === Category.All
            ? ids
            : intersection(categoryData[categoryKey], ids);

        acc.push(
          categorisedKeys
            .filter((id) => !!composedOccurrencesById[id])
            .map((id) => composedOccurrencesById[id])
        );
        return acc;
      }, [])
      .filter((arr) => arr.length !== 0);

    // Convert analysis group to insights for render
    const composedAndGroupedInsights = sortInsights(
      groupOccurrencesIntoInsights(
        composedAndGroupedOccurrences,
        state.groupBy
      ),
      state.groupBy,
      state.sortBy
    );

    // HACK: re-sort here to put priority 0 at the bottom
    const sorted =
      state.groupBy !== GroupBy.Priority
        ? composedAndGroupedInsights
        : composedAndGroupedInsights.sort((a, b) => {
            if (a[0].priority === 0) return 1;
            if (b[0].priority === 0) return -1;
            return 0;
          });

    // Dispatch processed data and grouped data
    dispatch({
      type: "SET_PROCESSED_INSIGHTS",
      occurrences: composedAndGroupedOccurrences,
      insights: sorted,
    });
  }, [
    state.isLoaded,
    state.isLoading,
    state.groupBy,
    state.search,
    state.isComposed,
    state.category,
  ]);

  // Synchronise composedInsights with the sortBy state
  React.useEffect(() => {
    if (!state.isProcessed) return;
    const composedAndGroupedInsights = sortInsights(
      state.composedAndGroupedInsights,
      state.groupBy,
      state.sortBy
    );

    // HACK: re-sort here to put priority 0 at the bottom
    const sorted =
      state.groupBy !== GroupBy.Priority
        ? composedAndGroupedInsights
        : composedAndGroupedInsights.sort((a, b) => {
            if (a[0].priority === 0) return 1;
            if (b[0].priority === 0) return -1;
            return 0;
          });

    dispatch({
      type: "SET_COMPOSED_AND_GROUPED_INSIGHTS",
      composedAndGroupedInsights: sorted,
    });
  }, [state.sortBy, state.isProcessed]);
}
